import * as THREE from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';

const DREI_PRESET_BASE =
  'https://raw.githubusercontent.com/pmndrs/drei-assets/master/hdri/';

const PRESET_FILES: Record<string, string> = {
  apartment: 'lebombo_1k.hdr',
  city: 'potsdamer_platz_1k.hdr',
  dawn: 'kiara_1_dawn_1k.hdr',
  forest: 'forest_slope_1k.hdr',
  lobby: 'st_fagans_interior_1k.hdr',
  night: 'dikhololo_night_1k.hdr',
  park: 'rooitou_park_1k.hdr',
  studio: 'studio_small_03_1k.hdr',
  sunset: 'venice_sunset_1k.hdr',
  warehouse: 'empty_warehouse_01_1k.hdr',
};

export const ENV_PRESET_NAMES = Object.keys(PRESET_FILES);

const cache = new Map<string, THREE.Texture>();
const pending = new Map<string, Promise<THREE.Texture | null>>();
const failedKeys = new Set<string>();

type ParsedEnvTex = {
  width: number;
  height: number;
  data?: ArrayBufferView;
  image?: THREE.DataTexture['image'];
  type?: THREE.TextureDataType;
  colorSpace?: THREE.ColorSpace;
  wrapS?: THREE.Wrapping;
  wrapT?: THREE.Wrapping;
  format?: THREE.PixelFormat;
  minFilter?: THREE.MinificationTextureFilter;
  magFilter?: THREE.MagnificationTextureFilter;
  generateMipmaps?: boolean;
  flipY?: boolean;
};

function envUrlLooksExr(url: string): boolean {
  const path = url.split('?')[0].split('#')[0].toLowerCase();
  return path.endsWith('.exr');
}

function applyParsedEnvData(texture: THREE.DataTexture, texData: ParsedEnvTex, isExr: boolean): void {
  if (texData.image !== undefined) {
    texture.image = texData.image;
  } else if (texData.data !== undefined) {
    texture.image = {
      width: texData.width,
      height: texData.height,
      data: texData.data,
    };
  } else {
    throw new Error('Env map parse returned no pixel data');
  }

  if (texData.type !== undefined) texture.type = texData.type;
  if (texData.format !== undefined) texture.format = texData.format;
  texture.wrapS = texData.wrapS ?? THREE.ClampToEdgeWrapping;
  texture.wrapT = texData.wrapT ?? THREE.ClampToEdgeWrapping;
  texture.magFilter = texData.magFilter ?? THREE.LinearFilter;
  texture.minFilter = texData.minFilter ?? THREE.LinearFilter;
  texture.generateMipmaps = texData.generateMipmaps ?? false;

  if (isExr) {
    texture.colorSpace = texData.colorSpace ?? THREE.LinearSRGBColorSpace;
    texture.flipY = texData.flipY ?? false;
  } else {
    texture.colorSpace = THREE.LinearSRGBColorSpace;
    texture.flipY = true;
  }

  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.needsUpdate = true;
}

function envUrlHasKnownExt(url: string): boolean {
  const path = url.split('?')[0].split('#')[0].toLowerCase();
  return path.endsWith('.hdr') || path.endsWith('.exr');
}

function tryParseEnvBuffer(buffer: ArrayBuffer, isExr: boolean): THREE.DataTexture | null {
  try {
    const parser = isExr ? new EXRLoader() : new RGBELoader();
    const texData = parser.parse(buffer) as ParsedEnvTex;
    const texture = new THREE.DataTexture();
    applyParsedEnvData(texture, texData, isExr);
    return texture;
  } catch {
    return null;
  }
}

/**
 * Fetch + parse env maps ourselves so bad parses resolve to null instead of
 * crashing Three's DataTextureLoader (PatchedRGBELoader returned null → null.image).
 */
async function fetchAndParseEnvMap(url: string): Promise<THREE.DataTexture | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const buffer = await res.arrayBuffer();
    if (envUrlLooksExr(url)) return tryParseEnvBuffer(buffer, true);
    if (url.split('?')[0].split('#')[0].toLowerCase().endsWith('.hdr')) {
      return tryParseEnvBuffer(buffer, false);
    }
    // Extensionless uploads: try HDR then EXR on the same bytes.
    const hdr = tryParseEnvBuffer(buffer, false);
    if (hdr) return hdr;
    return tryParseEnvBuffer(buffer, true);
  } catch (err) {
    console.warn('[loadEnvMap] Failed to load environment:', url, err);
    return null;
  }
}

export function loadEnvMap(presetOrUrl: string): Promise<THREE.Texture | null> {
  if (cache.has(presetOrUrl)) return Promise.resolve(cache.get(presetOrUrl)!);
  if (failedKeys.has(presetOrUrl)) return Promise.resolve(null);
  if (pending.has(presetOrUrl)) return pending.get(presetOrUrl)!;

  const url = PRESET_FILES[presetOrUrl]
    ? `${DREI_PRESET_BASE}${PRESET_FILES[presetOrUrl]}`
    : presetOrUrl;

  const p = fetchAndParseEnvMap(url).then((texture) => {
    pending.delete(presetOrUrl);
    if (texture) {
      cache.set(presetOrUrl, texture);
    } else {
      failedKeys.add(presetOrUrl);
    }
    return texture;
  });

  pending.set(presetOrUrl, p);
  return p;
}

export function getCachedEnvMap(presetOrUrl: string): THREE.Texture | null {
  return cache.get(presetOrUrl) ?? null;
}

export type EnvMapLoadState = 'idle' | 'loading' | 'loaded' | 'failed';

/** Editor UI: whether a preset/url env map is cached, loading, or failed. */
export function getEnvMapLoadState(presetOrUrl: string): EnvMapLoadState {
  if (!presetOrUrl) return 'idle';
  if (cache.has(presetOrUrl)) return 'loaded';
  if (failedKeys.has(presetOrUrl)) return 'failed';
  if (pending.has(presetOrUrl)) return 'loading';
  return 'idle';
}

export { envUrlHasKnownExt };

function cacheKeysForUrl(url: string): string[] {
  const keys = new Set<string>();
  keys.add(url);
  const bare = url.split('?')[0].split('#')[0];
  if (bare !== url) keys.add(bare);
  for (const key of cache.keys()) {
    if (key.split('?')[0].split('#')[0] === bare) keys.add(key);
  }
  for (const key of pending.keys()) {
    if (key.split('?')[0].split('#')[0] === bare) keys.add(key);
  }
  return [...keys];
}

/** Drop a cached env map after an asset replace (same logical slot, new file URL). */
export function invalidateEnvMapCache(url: string): void {
  for (const key of cacheKeysForUrl(url)) {
    const tex = cache.get(key);
    if (tex) tex.dispose();
    cache.delete(key);
    pending.delete(key);
    failedKeys.delete(key);
  }
}
